import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockIcon from '@mui/icons-material/Lock'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import EmployeeSelector from '../../events/EmployeeSelector'
import { SPEAK_UP_STATUSES, statusColor } from './speakUpConstants'
import { ReporterCell } from './SpeakUpConcerns'

const Field = ({ label, children }) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    <Box>{children}</Box>
  </Box>
)

const SpeakUpConcern = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const [concern, setConcern] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [outcome, setOutcome] = useState('')
  const [note, setNote] = useState('')
  const [assignee, setAssignee] = useState(null)

  useEffect(() => {
    API.get(`/speak-up-concerns/${id}`)
      .then((res) => {
        if (res.status === 200) {
          setConcern(res.data)
          setOutcome(res.data.outcome ?? '')
        }
      })
      .finally(() => setLoading(false))
  }, [id])

  const save = (body, successMessage) => {
    setSaving(true)
    return API.put(`/speak-up-concerns/${id}`, body)
      .then((res) => {
        if (res.status === 200) {
          setConcern(res.data)
          setOutcome(res.data.outcome ?? '')
          if (successMessage) setToast({ message: successMessage })
          return true
        }
        setToast({
          message: res.data?.error || 'Unable to update the concern',
          severity: 'error',
        })
        return false
      })
      .finally(() => setSaving(false))
  }

  if (loading) return <LinearProgress />
  if (!concern) return <Alert severity="error">Concern not found.</Alert>

  const assignedName = concern.assignedTo?.userAccount
    ? `${concern.assignedTo.userAccount.firstName} ${concern.assignedTo.userAccount.lastName}`
    : null

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/speak-up-concerns')}
        sx={{ alignSelf: 'flex-start' }}
      >
        All concerns
      </Button>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={1}
      >
        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="title2">{concern.referenceCode}</Typography>
            <Chip
              size="small"
              label={concern.status}
              color={statusColor(concern.status)}
            />
            {concern.anonymous && (
              <Chip
                size="small"
                icon={<LockIcon />}
                label="Anonymous"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {concern.category} · raised{' '}
            {moment(concern.createdAt).format('DD MMM YYYY, HH:mm')} via{' '}
            {concern.submittedVia === 'mobile'
              ? 'the mobile app'
              : 'the web app'}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={concern.status}
            label="Status"
            disabled={saving}
            onChange={(e) =>
              save({ status: e.target.value }, `Marked ${e.target.value}`)
            }
          >
            {SPEAK_UP_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {concern.anonymous && (
        <Alert severity="info" icon={<LockIcon />}>
          This concern was raised anonymously. AmbuNet holds no record of who
          submitted it — please do not try to work it out from the content, and
          do not record guesses in the notes below.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Stack gap={2}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title="The concern"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              />
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {concern.description}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={3}>
                    <Field label="When">
                      <Typography>
                        {concern.whenHappened || 'Not given'}
                      </Typography>
                    </Field>
                    <Field label="Where">
                      <Typography>
                        {concern.whereHappened || 'Not given'}
                      </Typography>
                    </Field>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Review notes"
                subheader="Visible to the governance team only."
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              />
              <Divider />
              <CardContent>
                <Stack gap={1.5}>
                  {(concern.notes ?? []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No notes yet.
                    </Typography>
                  )}
                  {(concern.notes ?? []).map((n) => (
                    <Stack
                      key={n._id}
                      direction="row"
                      gap={1}
                      alignItems="flex-start"
                    >
                      <Avatar
                        src={n.by?.photograph}
                        sx={{ width: 28, height: 28 }}
                      />
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          {n.by
                            ? `${n.by.firstName} ${n.by.lastName}`
                            : 'Unknown'}{' '}
                          · {moment(n.createdAt).format('DD MMM YYYY, HH:mm')}
                        </Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                          {n.note}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                  <TextField
                    multiline
                    minRows={2}
                    placeholder="Add a note on what has been done or found"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <Button
                    variant="outlined"
                    disabled={saving || !note.trim()}
                    onClick={() =>
                      save({ note: note.trim() }, 'Note added').then(
                        (ok) => ok && setNote(''),
                      )
                    }
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    Add note
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack gap={2}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Raised by"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              />
              <Divider />
              <CardContent>
                <Stack gap={1}>
                  <ReporterCell concern={concern} />
                  {!concern.anonymous && concern.reporter?.email && (
                    <Typography variant="body2" color="text.secondary">
                      {concern.reporter.email}
                    </Typography>
                  )}
                  {!concern.anonymous && concern.contactPreference && (
                    <Field label="Contact preference">
                      <Typography>{concern.contactPreference}</Typography>
                    </Field>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Assigned reviewer"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              />
              <Divider />
              <CardContent>
                <Stack gap={1}>
                  <Typography>
                    {assignedName ?? 'Nobody assigned yet'}
                  </Typography>
                  <EmployeeSelector
                    value={assignee}
                    onChange={(e, newValue) => setAssignee(newValue)}
                    label="Assign to"
                    size="small"
                    fullWidth
                    team={['Governance']}
                  />
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    {assignedName && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={saving}
                        onClick={() => save({ assignedTo: null }, 'Unassigned')}
                      >
                        Unassign
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving || !assignee}
                      onClick={() => {
                        const employeeID =
                          assignee?.employee?._id ??
                          (typeof assignee?.employee === 'string'
                            ? assignee.employee
                            : null)
                        if (!employeeID) {
                          setToast({
                            message: 'That person has no employee record',
                            severity: 'error',
                          })
                          return
                        }
                        save({ assignedTo: employeeID }, 'Assigned').then(
                          (ok) => ok && setAssignee(null),
                        )
                      }}
                    >
                      Assign
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Outcome"
                subheader="What was done about this concern. Recorded when it is closed."
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              />
              <Divider />
              <CardContent>
                <Stack gap={1}>
                  <TextField
                    multiline
                    minRows={3}
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    placeholder="e.g. Reviewed with the base supervisor; rota pattern changed from next month."
                  />
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={saving || outcome === (concern.outcome ?? '')}
                      onClick={() => save({ outcome }, 'Outcome saved')}
                    >
                      Save outcome
                    </Button>
                    {concern.status !== 'Closed' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={saving || !outcome.trim()}
                        onClick={() =>
                          save({ outcome, status: 'Closed' }, 'Concern closed')
                        }
                      >
                        Save & close
                      </Button>
                    )}
                  </Stack>
                  {concern.status === 'Closed' && concern.closedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Closed {moment(concern.closedAt).format('DD MMM YYYY')}
                      {concern.closedBy
                        ? ` by ${concern.closedBy.firstName} ${concern.closedBy.lastName}`
                        : ''}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SpeakUpConcern
